import type React from 'react';
import DoughnutChart from '@/components/UI/Charts/DoughnutChart/index.tsx';
import type { MKEERAApiResponseItem } from './dashboard.types';
import { useGenderPercentages } from './hooks/useGenderPercentages';
import StatCard from './StatCard';

const GENDER_COLORS = {
  Female: '#D9B64E', // gold
  Male: '#0FCA98', // teal
  Declined: '#808080', // gray
};

interface GenderChartProps {
  data: MKEERAApiResponseItem[];
  className?: string;
}

const GenderChart: React.FC<GenderChartProps> = ({ data, className }) => {
  const { male, female, declined } = useGenderPercentages(data);
  const chartData = [
    { label: 'Female', value: female, color: GENDER_COLORS.Female },
    { label: 'Male', value: male, color: GENDER_COLORS.Male },
    {
      label: 'Declined to Respond',
      value: declined,
      color: GENDER_COLORS.Declined,
    },
  ].filter((item) => item.value > 0); // Only show categories with data

  return (
    <StatCard className={className}>
      <div className='flex flex-row items-center w-full'>
        {/* Left: Legend and Title */}
        <div className='flex-1 min-w-0'>
          <div className='font-semibold text-lg text-neutralColor-darkest mb-4'>Gender</div>
          <div className='space-y-2'>
            {chartData.map((item) => (
              <div key={item.label} className='flex items-center space-x-2 text-neutralColor-medium text-sm'>
                <span className='inline-block w-3 h-3 rounded-full' style={{ background: item.color }} />
                <span className='font-medium text-neutralColor-darkest'>{item.label}</span>
                <span className='ml-1 text-neutralColor-normal'>: {item.value}%</span>
              </div>
            ))}
          </div>
        </div>
        {/* Right: Donut Chart */}
        <div className='shrink-0 ml-12 flex items-center justify-center'>
          <div className='w-[100px] h-[100px]'>
            <DoughnutChart
              dataLabels={chartData.map((d) => d.label)}
              datasets={chartData.map((d) => ({
                data: d.value,
                color: d.color,
              }))}
              display={false}
              percentage={true}
              legendPosition='right'
              legendAlign='center'
              fontSize={14}
              padding={0}
              cutout={'75%'}
              showValueAndPercentage={true}
            />
          </div>
        </div>
      </div>
    </StatCard>
  );
};

export default GenderChart;
